import { IPaginatedResponse, IPost } from '@sdk/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 6;

const PostService = sdk.getService('PostService');

interface StoreState {
  topic?: string;
  setTopic: (t?: string) => void;
  reset: () => void;
}

const store = createStore<StoreState>(set => ({
  topic: undefined,
  setTopic: topic => set({ topic }),
  reset: () => set({ topic: undefined })
}));

export const useStudentPostsList = () => {
  const { topic, setTopic, reset } = store();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['student-posts', { topic }],
    initialPageParam: DEFAULT_PAGE,
    queryFn: ({ pageParam = DEFAULT_PAGE }) =>
      PostService.getStudentPosts({ page: pageParam as number, limit: DEFAULT_LIMIT }, topic),
    getNextPageParam: lastPage => (lastPage.hasNextPage ? lastPage.nextPage : undefined)
  });

  const loadMorePosts = () => {
    if (isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  };

  const flatPages = (data?.pages ?? []) as IPaginatedResponse<IPost>[];
  const posts = flatPages.flatMap(p => p.docs);

  const lastPage = flatPages[flatPages.length - 1];

  return {
    posts,
    isPending: isLoading || isFetchingNextPage,
    isFetchingNextPage,
    hasNextPage,
    loadMorePosts,
    pagination: lastPage,
    topic,
    setTopic,
    reset
  };
};
